/**
 * BackButton — Universal navigation control for every internal page.
 *
 * Renders in the top-left of the page (both desktop + mobile). Clicking it
 * pops one entry from the browser history stack. When there is no
 * meaningful previous entry (deep link / direct load), it falls back to
 * the most sensible parent route for the current path so the user is
 * never stranded at the app's edge.
 *
 * Skipped automatically on the landing/root marketing routes — those are
 * "exit points", not internal pages.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

// Routes where we never render a back button.
const HIDDEN_ROUTES = new Set(["/", "/home", "/landing"]);

// Deep-link fallbacks. If history has no useful previous entry we route
// the user to the closest parent path.
const PARENT_FALLBACK_MAP = {
  "/dashboard": "/",
  "/admin": "/",
  "/directory": "/",
  "/blinterest": "/",
  "/pin": "/blinterest",
  "/board": "/blinterest",
  "/news": "/",
  "/article": "/news",
  "/business": "/directory",
  "/product": "/directory",
  "/cart": "/",
  "/checkout-success": "/",
  "/orders": "/dashboard",
  "/profile": "/dashboard",
  "/events": "/",
  "/founding-100": "/",
  "/founding-business": "/",
};

function parentRouteFor(pathname) {
  if (!pathname) return "/";
  // Match the longest prefix in our map (so `/board/xyz` → `/blinterest`, not `/`)
  const keys = Object.keys(PARENT_FALLBACK_MAP).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (pathname === key || pathname.startsWith(key + "/")) {
      return PARENT_FALLBACK_MAP[key];
    }
  }
  return "/";
}

export default function BackButton({ className = "", tone = "auto" }) {
  const navigate = useNavigate();
  const location = useLocation();

  if (HIDDEN_ROUTES.has(location.pathname)) return null;

  const onClick = () => {
    // Browser history has entries preceding the current page → go back.
    // window.history.length >= 2 means at least one prior entry (some
    // browsers include the initial about:blank so we defensively check
    // for the tab's history state).
    if (window.history.length > 1 && window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    // Otherwise land on the nearest logical parent.
    navigate(parentRouteFor(location.pathname));
  };

  // Tone: 'dark' on light backgrounds (default in Navbar), 'light' on
  // dark backgrounds. 'auto' picks 'dark' — Navbar bg is white.
  const isLight = tone === "light";
  const base = "inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors";
  const themed = isLight
    ? "text-white/85 hover:text-white hover:bg-white/10"
    : "text-tbc-navy/70 hover:text-tbc-navy hover:bg-tbc-navy/5";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} ${themed} ${className}`}
      aria-label="Back"
      data-testid="global-back-button"
    >
      <ChevronLeft className="w-4 h-4" />
      <span className="hidden sm:inline">Back</span>
    </button>
  );
}
